import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { UserPlus, Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react'

export default function Register() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validaciones
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      setError('Por favor, introduce tu nombre completo')
      return
    }

    if (!formData.email || !formData.email.includes('@')) {
      setError('Por favor, introduce un email valido')
      return
    }

    if (!formData.password || formData.password.length < 6) {
      setError('La contrasena debe tener al menos 6 caracteres')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contrasenas no coinciden')
      return
    }

    if (!acceptedTerms || !acceptedPrivacy) {
      setError('Debes aceptar los terminos y la politica de privacidad')
      return
    }

    setLoading(true)

    try {
      console.log('Intentando registrar usuario:', formData.email)
      
      // Registrar usuario con Supabase Auth
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName.trim(),
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        },
      })

      if (signUpError) {
        console.error('Error en signUp:', signUpError)
        throw signUpError
      }

      console.log('Usuario registrado:', data.user?.id)

      // Crear perfil en la tabla profiles
      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email: formData.email.trim(),
            full_name: formData.fullName.trim(),
            education_points: 0,
            participation_points: 0,
            change_score: 0,
          })

        if (profileError && profileError.code !== '23505') {
          console.error('Error creating profile:', profileError)
          // No bloqueamos el registro por error de perfil
        } else {
          console.log('Perfil creado exitosamente')
        }
      }

      setSuccess(true)
      // Si requiere confirmación de email, mostrar mensaje
      if (data.user && !data.user.confirmed_at) {
        setError('Revisa tu email para confirmar tu cuenta. Luego podras iniciar sesion.')
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        // Si no requiere confirmación, redirigir al dashboard
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      }
    } catch (err: any) {
      console.error('Error en registro:', err)
      setError(err.message || 'Error al crear la cuenta. Por favor, intentalo de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-education/5 via-white to-action/5 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo y header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-education rounded-2xl mb-4 shadow-lg">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Unete a la comunidad</h1>
          <p className="text-gray-600">Crea tu cuenta gratuita y comienza a generar impacto</p>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-green-800 font-semibold">Cuenta creada exitosamente</p>
                <p className="text-xs text-green-700 mt-1">
                  Revisa tu email para confirmar tu cuenta. Redirigiendo...
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-5">
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                Nombre completo
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent transition-all"
                  placeholder="Tu nombre completo"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent transition-all"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Contrasena
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent transition-all"
                  placeholder="Minimo 6 caracteres"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                Confirmar contrasena
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent transition-all"
                  placeholder="Repite tu contrasena"
                />
              </div>
            </div>

            {/* Consentimientos RGPD */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-education border-gray-300 rounded focus:ring-education"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Acepto los{' '}
                  <Link to="/terminos" className="text-education font-semibold hover:underline">
                    terminos y condiciones
                  </Link>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  className="mt-1 w-4 h-4 text-education border-gray-300 rounded focus:ring-education"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Acepto la{' '}
                  <Link to="/privacidad" className="text-education font-semibold hover:underline">
                    politica de privacidad
                  </Link>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-education text-white py-3 px-6 rounded-lg font-semibold hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta gratuita'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Ya tienes cuenta?{' '}
              <Link to="/login" className="text-education font-semibold hover:text-education/80 transition-colors">
                Inicia sesion
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <Link
              to="/"
              className="block text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
